import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Play, Pause, Maximize2, Volume2 } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
const chapters = [{
  time: '00:00',
  label: 'About the case',
  done: true
}, {
  time: '00:30',
  label: 'Challenge',
  done: true
}, {
  time: '01:00',
  label: 'Outcome',
  done: true
}, {
  time: '01:30',
  label: 'Clinician presentation',
  done: true
}, {
  time: '02:00',
  label: 'Case overview',
  done: true
}, {
  time: '02:30',
  label: 'Isolation & Prep',
  done: true
}, {
  time: '03:00',
  label: 'Layering Technique',
  active: true
}, {
  time: '03:30',
  label: 'Finishing & Polish'
}];
export function LayerCore() {
  return <section className="py-24 bg-white" data-id="element-37">
      <div className="max-w-7xl mx-auto px-6" data-id="element-38">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-39">
          <div className="flex items-center gap-3 mb-4" data-id="element-40">
            <div className="bg-solventum-dark text-solventum-green px-4 py-1 rounded-full text-sm font-bold" data-id="element-41">
              LAYER 2
            </div>
            <BookOpen className="w-5 h-5 text-solventum-teal" data-id="element-42" />
            <span className="text-solventum-dark/60 text-xs tracking-[0.2em] uppercase" data-id="element-43">
              Mobile · Player
            </span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-solventum-dark mb-4" data-id="element-44">
            The Clinical Core
          </h2>
          <p className="text-xl text-solventum-dark/60 max-w-3xl" data-id="element-45">
            The case itself — diagnosis to technique to outcome — formatted for
            the phone. Chapter-aware playback, clinician-narrated. Solventum
            appears naturally where it helped solve the case.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden ring-1 ring-solventum-gray bg-gradient-to-br from-solventum-lightTeal via-white to-solventum-lightTeal/40 py-16 px-6" data-id="element-46">
          <div className="relative grid lg:grid-cols-[1fr_auto_1fr] gap-10 items-center" data-id="element-47">
            <div className="hidden lg:block space-y-6 text-right" data-id="element-48">
              <FeatureRow title="Clinician-narrated" body="Voice and frame stay with the clinician — credibility intact." tone="light" data-id="element-49" />
              <FeatureRow title="Product in service" body="Solventum moments are surfaced as captions, never as ads." tone="light" data-id="element-50" />
            </div>

            <PhoneFrame data-id="element-51">
              <ClinicalCoreScreen data-id="element-52" />
            </PhoneFrame>

            <div className="hidden lg:block space-y-6" data-id="element-53">
              <FeatureRow title="Chapter map" body="Practitioners can skip straight to layering or finishing." align="left" tone="light" data-id="element-54" />
              <FeatureRow title="Diagnosis → outcome" body="The full clinical arc, from first photo to final polish." align="left" tone="light" data-id="element-55" />
              <FeatureRow title="Adapts to content" body="From video to clinical images and text — easy to scale and reuse across available content." align="left" tone="light" data-id="element-56" />
            </div>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-solventum-dark/60 mt-12 italic" data-id="element-57">
          The clinician tells the story. The product is in service of the case.
        </motion.p>
      </div>
    </section>;
}
function FeatureRow({
  title,
  body,
  align = 'right',
  tone = 'dark'
}: {
  title: string;
  body: string;
  align?: 'left' | 'right';
  tone?: 'light' | 'dark';
}) {
  const titleColor = tone === 'light' ? 'text-solventum-teal' : 'text-solventum-green';
  const bodyColor = tone === 'light' ? 'text-solventum-dark/70' : 'text-solventum-lightTeal/70';
  return <div className={align === 'right' ? 'ml-auto max-w-[240px]' : 'max-w-[240px]'} data-id="element-58">
      <div className={`${titleColor} text-[10px] font-semibold tracking-[0.25em] uppercase mb-2`} data-id="element-59">
        {title}
      </div>
      <div className={`${bodyColor} text-sm leading-relaxed`} data-id="element-60">{body}</div>
    </div>;
}
function ClinicalCoreScreen() {
  return <div className="relative w-full h-full bg-solventum-dark overflow-hidden" data-id="element-61">
      {/* Top spacer so video + caption sit lower on the phone screen */}
      <div className="h-20" data-id="element-62" />
      {/* Video area */}
      <div className="relative aspect-video bg-black" data-id="element-63">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://cdn.magicpatterns.com/uploads/8meJfA87U7KYCYdR4vr2wS/Sk%C3%A6rmbillede_2026-05-11_kl._12.44.36.png')"
      }} data-id="element-64" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" data-id="element-65" />
        <div className="absolute top-2 left-3 right-3 flex items-center justify-between" data-id="element-66">
          <div className="text-solventum-green text-[8px] font-bold tracking-[0.25em] uppercase" data-id="element-67">
            Chapter 07
          </div>
          <Maximize2 className="w-3 h-3 text-white/70" data-id="element-68" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-69">
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center ring-1 ring-white/20" data-id="element-70">
            <Pause className="w-4 h-4 text-white fill-white" data-id="element-71" />
          </div>
        </div>
        <div className="absolute bottom-2 left-3 right-3" data-id="element-72">
          <div className="h-[3px] bg-white/15 rounded-full overflow-hidden mb-1.5" data-id="element-73">
            <div className="h-full w-[75%] bg-solventum-green rounded-full" data-id="element-74" />
          </div>
          <div className="flex items-center justify-between text-white/70 text-[9px] font-mono" data-id="element-75">
            <span data-id="element-76">03:00</span>
            <Volume2 className="w-3 h-3" data-id="element-77" />
            <span data-id="element-78">04:00</span>
          </div>
        </div>
      </div>

      {/* Caption / now playing */}
      <div className="px-4 pt-3 pb-3 border-b border-white/5" data-id="element-79">
        <div className="flex items-center justify-between mb-1" data-id="element-80">
          <div className="text-solventum-green text-[8px] font-bold tracking-[0.2em] uppercase" data-id="element-81">
            Isolation & Prep
          </div>
          <div className="flex items-center gap-0.5" data-id="element-82">
            <span className="text-solventum-green font-editorial italic text-xs leading-none" data-id="element-83">
              Filtek
            </span>
            <span className="text-solventum-green text-[7px] leading-none" data-id="element-84">
              ™
            </span>
          </div>
        </div>
        <p className="text-white text-[12px] font-medium leading-snug mb-2" data-id="element-85">
          OC Clamp and Bioclear™ Diamond Wedge to isolate. Pre-wedge helps to
          gain visibility and better access for cavity restoration.
        </p>
        <div className="flex items-center gap-2" data-id="element-86">
          <div className="w-6 h-6 rounded-full bg-cover bg-center ring-1 ring-solventum-green/50" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&auto=format&fit=crop&q=80')"
        }} data-id="element-87" />
          <div className="text-white/80 text-[10px] font-semibold" data-id="element-88">
            Dr Chris O'Connor
          </div>
          <div className="text-white/40 text-[9px]" data-id="element-89">· Newcastle</div>
        </div>
      </div>

      {/* Chapters list — compact so video + caption stay centered */}
      <div className="px-4 pt-2 pb-2" data-id="element-90">
        <div className="flex items-center justify-between mb-1.5" data-id="element-91">
          <div className="text-white/50 text-[9px] tracking-[0.2em] uppercase" data-id="element-92">
            Chapters
          </div>
          <div className="text-white/40 text-[9px]" data-id="element-93">8 total</div>
        </div>
        <div className="space-y-1 max-h-[88px] overflow-y-auto pr-1" data-id="element-94">
          {chapters.map(ch => <div key={ch.label} className={`flex items-center gap-2 px-2 py-1.5 rounded-md border ${ch.active ? 'bg-solventum-green/15 border-solventum-green/40' : 'bg-white/[0.03] border-white/5'}`} data-id="element-95">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${ch.active ? 'bg-solventum-green' : ch.done ? 'bg-solventum-teal/30' : 'bg-white/10'}`} data-id="element-96">
                {ch.active ? <Play className="w-2 h-2 fill-solventum-dark text-solventum-dark" data-id="element-97" /> : <span className={`text-[8px] font-bold ${ch.done ? 'text-solventum-medTeal' : 'text-white/50'}`} data-id="element-98">
                    {ch.done ? '✓' : ''}
                  </span>}
              </div>
              <div className={`text-[10px] font-semibold flex-1 truncate ${ch.active ? 'text-white' : 'text-white/70'}`} data-id="element-99">
                {ch.label}
              </div>
              <div className={`text-[9px] font-mono ${ch.active ? 'text-solventum-green' : 'text-white/40'}`} data-id="element-100">
                {ch.time}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}